import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckoutManifest, startCheckout } from '@easypaypt/checkout-sdk';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Spinner, Modal, ModalHeader, ModalBody } from 'reactstrap';
import styled from 'styled-components';
import { Button } from '@mui/material';
import moment from 'moment';
import { Campaign, CampaignBase, CampaignChannel, CampaignGoal, CampaignRecipientEstimate, CampaignStatus, WhatsAppTemplateCatalogItem } from '../../models';
import { AutocompleteTextField, FormField, TextInputField } from '../../components/Forms/fields';
import { CheckInputField } from '../../components/Forms/fields/CheckInputField';
import Typography from '../../components/Typography';
import { useGeneralSettings } from '../../store/settings/hooks';
import { getEntitiesDataItems } from '../../store/entities/selectors';
import { ClientEntity } from '../../store/entities/state';
import { ApplicationState } from '../../store';
import { showError } from '../../store/entities/actions';
import { alertError, alertSuccess } from '../../store/alerts/actions';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { createClientSearchConfig } from '../../shared/clientSearch';
import { paths } from '../../app/constants';
import { createCampaignOrderAsync, deleteCampaignAsync, estimateCampaignRecipientsAsync, getCampaignAsync, getCampaignWhatsAppTemplateCatalogAsync, sendCampaignAsync, startCampaignOrderCheckoutAsync, testCampaignForCampaignAsync, updateCampaignAsync } from '../../ajax/requests';
import WhatsAppTemplatesSettings from '../Settings/WhatsAppTemplatesSettings';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmsIcon from '@mui/icons-material/Sms';
import { IconButton } from '@mui/material';
import { campaignGoalLabel } from './campaignsUtils';
import { CampaignBuilderFooter } from './CampaignBuilderFooter';
import { CampaignBuilderStepper } from './CampaignBuilderStepper';
import { CampaignAudienceSummary } from './CampaignAudienceSummary';
import { CampaignStatusBadge } from './CampaignStatusBadge';
import { CampaignReviewStep } from './CampaignReviewStep';
import { CampaignPreviewPanel } from './CampaignPreviewPanel';
import {
    clampCampaignBuilderStep,
    getCampaignDraftFingerprint,
    getUnknownCampaignPlaceholders,
    normalizeCampaignDraft,
    normalizeStringArray,
} from './campaignBuilderUtils';
import { getSelectedTemplate } from './whatsAppTemplateUtils';

const HtmlEditor = React.lazy(() => import('./HtmlEditor').then(m => ({ default: m.HtmlEditor })));

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const BuilderLayout = styled.div`
    display: grid;
    gap: var(--spacer-M);
`;

const BuilderGrid = styled.div`
    display: grid;
    gap: var(--spacer-M);
`;

const SummaryCard = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--spacer-M);
    padding: var(--spacer-S) var(--spacer-M);
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: var(--spacer-XS);
    background: var(--bg-light);
`;

const SummaryItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex-shrink: 0;
`;

const SummaryLabel = styled.div`
    font-size: 0.72rem;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 600;
`;

const SummaryValue = styled.div`
    font-weight: 700;
    color: var(--text-body);
    font-size: 0.92rem;
`;

const SummaryDivider = styled.div`
    width: var(--border-thin);
    height: 2rem;
    background: var(--appLigthGrey);
    flex-shrink: 0;
`;

const SummaryHint = styled.div`
    flex: 1;
    min-width: 140px;
    font-size: 0.82rem;
    color: var(--text-muted);
    font-style: italic;
`;

const StepCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-M);
    padding: var(--spacer-M);
    border: 1px solid var(--appLigthGrey);
    border-radius: var(--spacer-XS);
    background: white;
`;

const PanelCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
    padding: var(--spacer-M);
    border: 1px solid var(--appLigthGrey);
    border-radius: var(--spacer-XS);
    background: var(--bg-light);
`;

const SectionTitle = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--spacer-S);
    font-weight: 800;
`;

const FieldsGrid = styled.div`
    display: grid;
    gap: var(--spacer-S);
`;

const InlineChecks = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-XS);
`;

const ChannelPickerRow = styled.div`
    display: flex;
    gap: var(--spacer-S);
`;

const ChannelButton = styled.button<{ $selected: boolean } & React.ButtonHTMLAttributes<HTMLButtonElement>>`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacer-XS);
    padding: var(--spacer-M);
    min-width: 120px;
    border-radius: var(--spacer-XS);
    border: var(--border-medium) solid ${props => props.$selected ? 'var(--appBlue)' : 'var(--appLigthGrey)'};
    background: ${props => props.$selected ? 'var(--bg-hover-light)' : 'transparent'};
    cursor: pointer;
    color: ${props => props.$selected ? 'var(--appBlue)' : 'var(--text-muted)'};
    opacity: ${props => props.$selected ? 1 : 0.45};
    font-size: 0.85rem;
    font-weight: 600;
    transition: border-color 0.15s, opacity 0.15s, background 0.15s;

    &:hover {
        opacity: 1;
        border-color: var(--appBlue);
    }

    svg {
        font-size: 2.4rem;
    }
`;

const EmailPreviewRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-XS);
`;


const ErrorList = styled.ul`
    margin: 0;
    padding-left: 1.2rem;
    color: var(--danger-color);
`;

const builderGoals = [
    CampaignGoal.Custom,
    CampaignGoal.ReactivateClients,
    CampaignGoal.PromoteService,
    CampaignGoal.AnnounceNews,
    CampaignGoal.BirthdayOrSeasonal,
].map(goal => String(goal));

type BuilderParams = {
    id: string;
};

const getGoalValue = (campaign: CampaignBase): string => String(campaign.goal ?? CampaignGoal.Custom);

const hasValidEmail = (value?: string): boolean => !value || EMAIL_REGEX.test(value);

const isWhatsAppCampaign = (campaign?: CampaignBase): boolean => campaign?.channel === CampaignChannel.WhatsApp;
const isSmsCampaign = (campaign?: CampaignBase): boolean => campaign?.channel === CampaignChannel.SMS;


const validateStepOne = (campaign: CampaignBase): TK[] => {
    const issues: TK[] = [];
    if (!campaign.name?.trim()) issues.push(TK.campaignNameRequired);
    return issues;
};

const validateStepThree = (campaign: CampaignBase): TK[] => {
    const issues: TK[] = [];

    if (isWhatsAppCampaign(campaign)) {
        if (!campaign.templateId?.trim()) {
            issues.push(TK.campaignWhatsAppTemplateRequired);
        }
        return issues;
    }

    if (isSmsCampaign(campaign)) {
        if (!campaign.content?.trim()) {
            issues.push(TK.campaignSmsBodyRequired);
        }
        return issues;
    }

    if (!campaign.subject?.trim()) issues.push(TK.campaignSubjectRequired);
    if (!campaign.content?.trim()) issues.push(TK.campaignContentRequired);
    if (!hasValidEmail(campaign.senderEmail)) issues.push(TK.campaignInvalidSenderEmail);

    const unknownPlaceholders = getUnknownCampaignPlaceholders(campaign.subject, campaign.preheader, campaign.content);
    if (unknownPlaceholders.length > 0) issues.push(TK.campaignUnknownPlaceholders);

    return issues;
};

const validateManualEmails = (campaign: CampaignBase): TK[] => {
    if (isWhatsAppCampaign(campaign) || isSmsCampaign(campaign)) {
        return [];
    }

    const invalidEmails = normalizeStringArray(campaign.recipientsAdditionalEmails).filter(email => !EMAIL_REGEX.test(email));
    return invalidEmails.length > 0 ? [TK.campaignInvalidManualEmails] : [];
};

const validationIssuesForStep = (step: number, campaign: Campaign, estimate?: CampaignRecipientEstimate): TK[] => {
    switch (step) {
        case 1:
            return validateStepOne(campaign);
        case 2:
            return validateManualEmails(campaign);
        case 3:
            return validateStepThree(campaign);
        case 4: {
            const issues = [...validateStepOne(campaign), ...validateStepThree(campaign), ...validateManualEmails(campaign)];
            if (estimate && estimate.totalIncluded === 0) issues.push(TK.campaignNoEligibleRecipients);
            if (campaign.status === CampaignStatus.Archived) issues.push(TK.campaignArchivedCannotSend);
            if ([CampaignStatus.Sending, CampaignStatus.Sent, CampaignStatus.PartiallyFailed, CampaignStatus.Failed].includes(campaign.status)) issues.push(TK.campaignAlreadySentCannotSend);
            return issues;
        }
        default:
            return [];
    }
};

const buildSavePayload = (campaign: Campaign): CampaignBase => normalizeCampaignDraft({
    ...campaign,
    builderStep: clampCampaignBuilderStep(campaign.builderStep),
});

export const CampaignBuilderPage: React.FC = () => {
    const { id } = useParams<BuilderParams>();
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const t = useTranslations();
    const isNewCampaign = new URLSearchParams(location.search).get('new') === '1';

    const clients = useSelector(getEntitiesDataItems<ClientEntity>('clients'));
    const { clientTags } = useGeneralSettings();
    const remindersSettings = useSelector((state: ApplicationState) => state.settings.reminders);

    const [campaign, setCampaign] = useState<Campaign | undefined>();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sending, setSending] = useState(false);
    const [testing, setTesting] = useState(false);
    const [estimate, setEstimate] = useState<CampaignRecipientEstimate | undefined>();
    const [estimateLoading, setEstimateLoading] = useState(false);
    const [testEmail, setTestEmail] = useState('');
    const [errorKey, setErrorKey] = useState<TK | undefined>();
    const [savedFingerprint, setSavedFingerprint] = useState('');
    const [templateCatalog, setTemplateCatalog] = useState<WhatsAppTemplateCatalogItem[]>([]);
    const [templateCatalogLoading, setTemplateCatalogLoading] = useState(false);
    const [paying, setPaying] = useState(false);
    const [paymentDone, setPaymentDone] = useState(false);
    const [checkoutManifest, setCheckoutManifest] = useState<(CheckoutManifest & { testMode?: boolean }) | null>(null);
    const [showTemplatesSettings, setShowTemplatesSettings] = useState(false);
    const isMountedRef = useRef(true);

    const clientSearchConfig = useMemo(() => createClientSearchConfig(clients), [clients]);
    const currentStep = clampCampaignBuilderStep(campaign?.builderStep);
    const selectedWhatsAppTemplate = useMemo(() => getSelectedTemplate(templateCatalog, campaign?.templateId), [campaign?.templateId, templateCatalog]);
    const dirty = !!campaign && getCampaignDraftFingerprint(campaign) !== savedFingerprint;
    const stepIssues = useMemo(() => campaign ? validationIssuesForStep(currentStep, campaign, estimate) : [], [campaign, currentStep, estimate]);
    const readinessIssues = useMemo(() => campaign ? validationIssuesForStep(4, campaign, estimate) : [], [campaign, estimate]);

    const loadCampaign = React.useCallback(async () => {
        setLoading(true);
        setErrorKey(undefined);

        try {
            const data = await getCampaignAsync(id);
            const normalized = normalizeCampaignDraft(data) as Campaign;
            const withPrefill: Campaign = {
                ...normalized,
                senderName: normalized.senderName || remindersSettings?.emailName || '',
                senderEmail: normalized.senderEmail || remindersSettings?.emailFrom || '',
            };
            setCampaign(withPrefill);
            setSavedFingerprint(getCampaignDraftFingerprint(withPrefill));
            setTestEmail(withPrefill.senderEmail ?? '');
            setEstimate(undefined);
        } catch (error) {
            console.error(error);
            dispatch(showError(error));
            setErrorKey(TK.campaignNotFound);
        } finally {
            setLoading(false);
        }
    }, [dispatch, id, remindersSettings?.emailName, remindersSettings?.emailFrom]);

    const loadTemplateCatalog = React.useCallback(async () => {
        if (!campaign || campaign.channel !== CampaignChannel.WhatsApp) {
            setTemplateCatalog([]);
            return;
        }

        setTemplateCatalogLoading(true);
        try {
            const data = await getCampaignWhatsAppTemplateCatalogAsync();
            setTemplateCatalog(data);
        } catch (error) {
            console.error(error);
            dispatch(showError(error));
        } finally {
            setTemplateCatalogLoading(false);
        }
    }, [campaign?.channel, dispatch]);

    useEffect(() => {
        void loadCampaign();
    }, [loadCampaign]);

    useEffect(() => {
        void loadTemplateCatalog();
    }, [loadTemplateCatalog]);

    const updateCampaign = React.useCallback((updater: (current: Campaign) => Campaign) => {
        setCampaign(current => current ? updater(current) : current);
    }, []);

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (!dirty) return;
            event.preventDefault();
            event.returnValue = '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [dirty]);

    const mapClientIdsToOptions = (clientIds?: string[]) => (clientIds ?? [])
        .map(clientId => clientSearchConfig.getOptionByClientId(clientId))
        .filter((option): option is string => !!option);

    const mapOptionsToClientIds = (options: string[]) => normalizeStringArray(options)
        .map(option => clientSearchConfig.getClientByOption(option)?.id)
        .filter((clientId): clientId is string => !!clientId);

    const updateWhatsAppTemplateSelection = React.useCallback((templateId: string) => {
        const template = templateCatalog.find(item => item.id === templateId);

        updateCampaign(current => ({
            ...current,
            templateId: template?.id,
            templateVersion: template ? String(template.version) : undefined,
            templateLanguage: template?.language,
            templateParameters: [],
        }));
    }, [templateCatalog, updateCampaign]);

    const persistCampaign = React.useCallback(async (): Promise<boolean> => {
        if (!campaign) return false;

        const payload = buildSavePayload(campaign);
        setSaving(true);
        try {
            const noOfRecipients = await updateCampaignAsync(id, payload);
            const updated = normalizeCampaignDraft({
                ...campaign,
                ...payload,
            } as CampaignBase) as Campaign;
            updated.noOfRecipients = noOfRecipients;
            setCampaign(updated);
            setSavedFingerprint(getCampaignDraftFingerprint(updated));
            dispatch(alertSuccess(t(TK.savedSuccessfully)));
            if (isNewCampaign) {
                history.replace(location.pathname);
            }
            return true;
        } catch (error) {
            console.error(error);
            dispatch(showError(error));
            return false;
        } finally {
            setSaving(false);
        }
    }, [campaign, dispatch, id, t]);

    const refreshEstimate = React.useCallback(async (currentCampaign: Campaign) => {
        setEstimateLoading(true);
        try {
            const data = await estimateCampaignRecipientsAsync(id, buildSavePayload(currentCampaign));
            setEstimate(data);
            setCampaign(current => current ? ({ ...current, lastRecipientEstimate: data.totalIncluded } as Campaign) : current);
        } catch (error) {
            console.error(error);
            dispatch(showError(error));
        } finally {
            setEstimateLoading(false);
        }
    }, [dispatch, id]);

    useEffect(() => {
        if (!campaign || currentStep < 2) return;

        const snapshot = campaign;
        const timeout = window.setTimeout(() => {
            void refreshEstimate(snapshot);
        }, 300);

        return () => window.clearTimeout(timeout);
    }, [
        campaign?.recipientsAllClients,
        campaign?.recipientsClientsTagsToInclude,
        campaign?.recipientsClientsIdsToInclude,
        campaign?.recipientsClientsIdsToIgnore,
        campaign?.recipientsAdditionalEmails,
        campaign?.includeClientsWithoutMarketingConsentSubmitted,
        campaign?.ignoreMarketingConsent,
        campaign?.consentMode,
        currentStep,
        refreshEstimate,
    ]);

    const handleBack = () => {
        if (!campaign || currentStep <= 1) return;
        updateCampaign(current => ({ ...current, builderStep: currentStep - 1 }));
    };

    const handleNext = () => {
        if (!campaign || stepIssues.length > 0) {
            if (stepIssues.length > 0) dispatch(alertError(t(stepIssues[0])));
            return;
        }

        if (currentStep >= 4) return;
        updateCampaign(current => ({ ...current, builderStep: currentStep + 1 }));
    };

    const handleDiscard = () => {
        if (dirty && !window.confirm(t(TK.campaignDiscardChangesConfirm))) {
            return;
        }

        if (isNewCampaign && campaign?.status === CampaignStatus.Draft) {
            void deleteCampaignAsync(id).catch(console.error);
        }

        history.push(paths.campaigns());
    };

    const handleGoToStep = (step: number) => {
        if (!campaign || step === currentStep) return;
        if (step < currentStep) {
            updateCampaign(current => ({ ...current, builderStep: step }));
        } else if (step === currentStep + 1) {
            handleNext();
        }
    };

    const handleTestEmail = async () => {
        if (!campaign || !testEmail.trim()) return;

        const invalid = !EMAIL_REGEX.test(testEmail.trim());
        if (invalid) {
            dispatch(alertError(t(TK.campaignInvalidTestEmail)));
            return;
        }

        setTesting(true);
        try {
            if (dirty) {
                const saved = await persistCampaign();
                if (!saved) return;
            }
            await testCampaignForCampaignAsync(id, testEmail.trim());
            dispatch(alertSuccess(t(TK.sent)));
        } catch (error) {
            console.error(error);
            dispatch(showError(error));
        } finally {
            setTesting(false);
        }
    };

    useEffect(() => () => { isMountedRef.current = false; }, []);

    const handleSendNow = async () => {
        if (!campaign) return;
        if (readinessIssues.length > 0) {
            dispatch(alertError(t(readinessIssues[0])));
            return;
        }

        setSending(true);
        try {
            if (dirty) {
                const saved = await persistCampaign();
                if (!saved) return;
            }

            await sendCampaignAsync(id);
            dispatch(alertSuccess(t(TK.sent)));
            history.push(paths.campaignAnalytics(id));
        } catch (error) {
            console.error(error);
            dispatch(showError(error));
        } finally {
            setSending(false);
        }
    };

    const handlePayAndSend = async () => {
        if (!campaign || !estimate) return;
        if (readinessIssues.length > 0) {
            dispatch(alertError(t(readinessIssues[0])));
            return;
        }

        setPaying(true);
        try {
            if (dirty) {
                const saved = await persistCampaign();
                if (!saved) return;
            }

            const order = await createCampaignOrderAsync(id, estimate.totalIncluded);
            const manifest = await startCampaignOrderCheckoutAsync(id, order.id);

            // The div is always mounted so the SDK can find it immediately.
            // Show the overlay after startCheckout so the widget is ready when it appears.
            const checkoutInstance = startCheckout(manifest, {
                id: 'easypay-campaign-checkout',
                testing: manifest.testMode ?? false,
                onSuccess: () => {
                    checkoutInstance?.unmount();
                    setCheckoutManifest(null);
                    if (isMountedRef.current) {
                        setPaymentDone(true);
                        dispatch(alertSuccess(t(TK.campaignPaySuccess)));
                    }
                },
                onClose: () => {
                    checkoutInstance?.unmount();
                    setCheckoutManifest(null);
                    if (isMountedRef.current) {
                        dispatch(alertError(t(TK.campaignPayCancelled)));
                    }
                },
                onError: () => {
                    checkoutInstance?.unmount();
                    setCheckoutManifest(null);
                    if (isMountedRef.current) {
                        dispatch(alertError(t(TK.campaignPayError)));
                    }
                },
            });
            setCheckoutManifest(manifest);
        } catch (error) {
            console.error(error);
            dispatch(showError(error));
        } finally {
            if (isMountedRef.current) {
                setPaying(false);
            }
        }
    };

    const cooldownEndsAt = useMemo(() => {
        if (!campaign?.lastTriggeredAt) return undefined;
        return moment(campaign.lastTriggeredAt).add(10, 'minutes').format('YYYY-MM-DDTHH:mm');
    }, [campaign?.lastTriggeredAt]);

    const isOnCooldown = useMemo(() => {
        if (!cooldownEndsAt) return false;
        return moment().isBefore(moment(cooldownEndsAt));
    }, [cooldownEndsAt]);

    const canSendNow = !!campaign && currentStep === 4 && readinessIssues.length === 0 && !isOnCooldown && !!estimate && !estimateLoading && !saving && !sending && !paying && !estimate.requiresPayment;
    const canPayAndSend = !!campaign && currentStep === 4 && readinessIssues.length === 0 && !isOnCooldown && !!estimate && !estimateLoading && !saving && !sending && !paying && estimate.requiresPayment && !paymentDone && campaign.status !== CampaignStatus.Paid;
    const canSendAfterPayment = !!campaign && currentStep === 4 && readinessIssues.length === 0 && !isOnCooldown && !!estimate && !estimateLoading && !saving && !sending && estimate.requiresPayment && (paymentDone || campaign.status === CampaignStatus.Paid);
    const canViewAnalytics = !!campaign?.lastTriggeredAt;

    const handleViewAnalytics = React.useCallback(() => {
        history.push(paths.campaignAnalytics(id));
    }, [history, id]);

    if (loading && !campaign) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--spacer-L)' }}>
                <Spinner />
            </div>
        );
    }

    if (errorKey) {
        return (
            <PanelCard>
                <Typography size="large">{t(errorKey)}</Typography>
                <Button variant="contained" onClick={() => history.push(paths.campaigns())}>
                    {t(TK.back)}
                </Button>
            </PanelCard>
        );
    }

    if (!campaign) {
        return null;
    }

    return (
        <BuilderLayout>
            <SectionTitle>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-S)', flexWrap: 'wrap' }}>
                    <Typography size="large">{campaign.name || t(TK.campaign)}</Typography>
                    <CampaignStatusBadge status={campaign.status} />
                </div>
                <IconButton aria-label={t(TK.close)} onClick={handleDiscard} size="small">
                    <CloseIcon />
                </IconButton>
            </SectionTitle>

            <CampaignBuilderStepper
                activeStep={currentStep}
                canGoBack={currentStep > 1}
                canGoNext={currentStep < 4}
                loading={loading || saving || sending}
                onBack={handleBack}
                onNext={handleNext}
                onStepClick={handleGoToStep}
            />

            <BuilderGrid>
                <StepCard>

                    {currentStep === 1 ? (
                        <FieldsGrid>
                            <TextInputField
                                id="campaign-name"
                                label={t(TK.campaignName)}
                                value={campaign.name ?? ''}
                                onChange={name => updateCampaign(current => ({ ...current, name }))}
                            />
                            <TextInputField
                                id="campaign-internal-description"
                                        label={t(TK.internalDescription)}
                                type="textarea"
                                value={campaign.internalDescription ?? ''}
                                onChange={internalDescription => updateCampaign(current => ({ ...current, internalDescription }))}
                            />
                            <AutocompleteTextField
                                id="campaign-goal"
                                label={t(TK.goal)}
                                value={getGoalValue(campaign)}
                                options={builderGoals}
                                onValueChanged={goal => updateCampaign(current => ({ ...current, goal: Number(goal) as CampaignGoal }))}
                                optionLabelResolver={goal => t(campaignGoalLabel(Number(goal) as CampaignGoal))}
                            />
                            <div>
                                <Typography size="small">{t(TK.campaignSendUsing)}</Typography>
                                <ChannelPickerRow>
                                    <ChannelButton
                                        type="button"
                                        $selected={campaign.channel === CampaignChannel.Email}
                                        onClick={() => updateCampaign(current => ({ ...current, channel: CampaignChannel.Email, content: '' }))}
                                    >
                                        <EmailIcon />
                                        {t(TK.email)}
                                    </ChannelButton>
                                    <ChannelButton
                                        type="button"
                                        $selected={campaign.channel === CampaignChannel.WhatsApp}
                                        onClick={() => updateCampaign(current => ({ ...current, channel: CampaignChannel.WhatsApp, content: '' }))}
                                    >
                                        <WhatsAppIcon />
                                        {t(TK.whatsApp)}
                                    </ChannelButton>
                                    <ChannelButton
                                        type="button"
                                        $selected={campaign.channel === CampaignChannel.SMS}
                                        onClick={() => updateCampaign(current => ({ ...current, channel: CampaignChannel.SMS, content: '' }))}
                                    >
                                        <SmsIcon />
                                        SMS
                                    </ChannelButton>
                                </ChannelPickerRow>
                            </div>
                        </FieldsGrid>
                    ) : null}

                    {currentStep === 2 ? (
                        <FieldsGrid>
                            <InlineChecks>
                                <CheckInputField
                                    label={t(TK.allClients)}
                                    value={campaign.recipientsAllClients ?? false}
                                    onChange={recipientsAllClients => updateCampaign(current => ({ ...current, recipientsAllClients }))}
                                />
                                <CheckInputField
                                    label={t(TK.campaignIncludeClientsWithoutConsentSubmitted)}
                                    value={campaign.includeClientsWithoutMarketingConsentSubmitted ?? false}
                                    onChange={includeClientsWithoutMarketingConsentSubmitted => updateCampaign(current => ({ ...current, includeClientsWithoutMarketingConsentSubmitted }))}
                                />
                                <CheckInputField
                                    label={t(TK.campaignIgnoreMarketingConsent)}
                                    value={campaign.ignoreMarketingConsent ?? false}
                                    onChange={ignoreMarketingConsent => updateCampaign(current => ({ ...current, ignoreMarketingConsent }))}
                                />
                            </InlineChecks>

                            {!campaign.recipientsAllClients ? (
                                <>
                                    <AutocompleteTextField
                                        label={t(TK.clientTags)}
                                        multiSelect
                                        values={normalizeStringArray(campaign.recipientsClientsTagsToInclude)}
                                        options={Object.keys(clientTags)}
                                        onValuesChanged={values => updateCampaign(current => ({ ...current, recipientsClientsTagsToInclude: normalizeStringArray(values) }))}
                                    />
                                    <AutocompleteTextField
                                        label={t(TK.includeClients)}
                                        multiSelect
                                        values={mapClientIdsToOptions(campaign.recipientsClientsIdsToInclude)}
                                        options={clientSearchConfig.options}
                                        optionLabelResolver={clientSearchConfig.optionLabelResolver}
                                        searchTextResolver={clientSearchConfig.searchTextResolver}
                                        searchMatcher={clientSearchConfig.searchMatcher}
                                        onValuesChanged={options => updateCampaign(current => ({ ...current, recipientsClientsIdsToInclude: mapOptionsToClientIds(options) }))}
                                    />
                                </>
                            ) : null}

                            <AutocompleteTextField
                                label={t(TK.excludeClients)}
                                multiSelect
                                values={mapClientIdsToOptions(campaign.recipientsClientsIdsToIgnore)}
                                options={clientSearchConfig.options}
                                optionLabelResolver={clientSearchConfig.optionLabelResolver}
                                searchTextResolver={clientSearchConfig.searchTextResolver}
                                searchMatcher={clientSearchConfig.searchMatcher}
                                onValuesChanged={options => updateCampaign(current => ({ ...current, recipientsClientsIdsToIgnore: mapOptionsToClientIds(options) }))}
                            />

                            <AutocompleteTextField
                                label={(isWhatsAppCampaign(campaign) || isSmsCampaign(campaign)) ? t(TK.campaignIncludePhoneNumbers) : t(TK.includeEmails)}
                                multiSelect
                                values={normalizeStringArray(campaign.recipientsAdditionalEmails)}
                                options={[]}
                                onAddNewOption={email => updateCampaign(current => ({ ...current, recipientsAdditionalEmails: normalizeStringArray([...(current.recipientsAdditionalEmails ?? []), email]) }))}
                                onValuesChanged={values => updateCampaign(current => ({ ...current, recipientsAdditionalEmails: normalizeStringArray(values) }))}
                            />

                            <PanelCard>
                                <Typography size="small">{t(TK.campaignAudienceDisclaimer)}</Typography>
                                <Typography size="small">{t(TK.campaignAudienceEstimateHint)}</Typography>
                                <Typography size="small">
                                    {estimateLoading
                                        ? t(TK.loading)
                                        : estimate
                                            ? t(TK.campaignAudienceEstimateUpdated)
                                            : t(TK.campaignAudienceEstimatePending)}
                                </Typography>
                                <CampaignAudienceSummary estimate={estimate} />
                            </PanelCard>
                        </FieldsGrid>
                    ) : null}

                    {currentStep === 3 ? (
                        <FieldsGrid>
                            {isSmsCampaign(campaign) ? (
                                <>
                                    <TextInputField
                                        id="campaign-sms-body"
                                        label={t(TK.campaignSmsBody)}
                                        type="textarea"
                                        value={campaign.content ?? ''}
                                        onChange={content => updateCampaign(current => ({ ...current, content }))}
                                        helpText={t(TK.campaignSmsBodyHint)}
                                        placeholder={t(TK.campaignSmsBodyPlaceholder)}
                                    />
                                    <CampaignPreviewPanel campaign={campaign} />
                                    {stepIssues.length > 0 ? (
                                        <PanelCard>
                                            <ErrorList>
                                                {stepIssues.map(issue => <li key={issue}>{t(issue)}</li>)}
                                            </ErrorList>
                                        </PanelCard>
                                    ) : null}
                                </>
                            ) : isWhatsAppCampaign(campaign) ? (
                                <>
                                    <PanelCard>
                                        <Typography size="small">{t(TK.campaignWhatsAppTemplateDisclaimer)}</Typography>
                                        <AutocompleteTextField
                                            id="campaign-whatsapp-template"
                                            label={t(TK.whatsappTemplate)}
                                            value={campaign.templateId ?? ''}
                                            options={templateCatalog.map(template => template.id)}
                                            onValueChanged={updateWhatsAppTemplateSelection}
                                            optionLabelResolver={templateId => {
                                                const template = templateCatalog.find(item => item.id === templateId);
                                                return template ? `${template.displayName} - ${template.language}` : templateId;
                                            }}
                                            noOptionsText={templateCatalogLoading ? t(TK.loading) : t(TK.notFoundInSet)}
                                            disabled={saving || sending}
                                        />

                                        {templateCatalog.length === 0 && !templateCatalogLoading ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XS)' }}>
                                                <Typography size="small">{t(TK.whatsappTemplatesNoCampaignTemplates)}</Typography>
                                                <Button
                                                    variant="contained"
                                                    onClick={() => setShowTemplatesSettings(true)}
                                                >
                                                    {t(TK.whatsappTemplatesOpenSettings)}
                                                </Button>
                                            </div>
                                        ) : null}
                                    </PanelCard>

                                    {selectedWhatsAppTemplate ? (
                                        <CampaignPreviewPanel campaign={campaign} whatsappTemplate={selectedWhatsAppTemplate} />
                                    ) : null}

                                    {stepIssues.length > 0 ? (
                                        <PanelCard>
                                            <ErrorList>
                                                {stepIssues.map(issue => <li key={issue}>{t(issue)}</li>)}
                                            </ErrorList>
                                        </PanelCard>
                                    ) : null}
                                </>
                            ) : (
                                <>
                                    <TextInputField
                                        id="campaign-subject"
                                        label={t(TK.subject)}
                                        value={campaign.subject ?? ''}
                                        onChange={subject => updateCampaign(current => ({ ...current, subject }))}
                                    />
                                    <TextInputField
                                        id="campaign-preheader"
                                        label={`${t(TK.preheader)} (${t(TK.optional)})`}
                                        value={campaign.preheader ?? ''}
                                        onChange={preheader => updateCampaign(current => ({ ...current, preheader }))}
                                        helpText={t(TK.campaignPreheaderHint)}
                                    />
                                    <TextInputField
                                        id="campaign-sender-name"
                                        label={`${t(TK.senderName)} (${t(TK.optional)})`}
                                        value={campaign.senderName ?? ''}
                                        onChange={senderName => updateCampaign(current => ({ ...current, senderName }))}
                                        helpText={t(TK.campaignSenderNameHint)}
                                    />
                                    <TextInputField
                                        id="campaign-sender-email"
                                        label={`${t(TK.senderEmail)} (${t(TK.optional)})`}
                                        value={campaign.senderEmail ?? ''}
                                        onChange={senderEmail => updateCampaign(current => ({ ...current, senderEmail }))}
                                        helpText={t(TK.campaignSenderEmailHint)}
                                    />

                                    {stepIssues.length > 0 ? (
                                        <PanelCard>
                                            <ErrorList>
                                                {stepIssues.map(issue => <li key={issue}>{t(issue)}</li>)}
                                            </ErrorList>
                                        </PanelCard>
                                    ) : null}

                                    <FormField label={t(TK.preview)} helpNotes={t(TK.campaignPreviewPlaceholdersHint)}>
                                        <React.Suspense fallback={<div style={{ padding: 'var(--spacer-M)', textAlign: 'center' }}><Spinner /></div>}>
                                            <HtmlEditor
                                                readOnly={saving || sending}
                                                value={campaign.content ?? ''}
                                                onChange={content => updateCampaign(current => ({ ...current, content }))}
                                            />
                                        </React.Suspense>
                                    </FormField>

                                    <EmailPreviewRow>
                                        <TextInputField
                                            id="campaign-test-email"
                                            label={t(TK.testEmail)}
                                            value={testEmail}
                                            onChange={setTestEmail}
                                            placeholder="xpto@domain.com"
                                        />
                                        <Button variant="contained" disabled={testing} onClick={handleTestEmail}>
                                            {t(TK.testEmail)}
                                        </Button>
                                    </EmailPreviewRow>
                                </>
                            )}
                        </FieldsGrid>
                    ) : null}

                    {currentStep === 4 && (
                        <CampaignReviewStep
                            campaign={campaign}
                            estimate={estimate}
                            whatsAppTemplate={selectedWhatsAppTemplate}
                            readinessIssues={readinessIssues}
                            paymentDone={paymentDone}
                            isOnCooldown={isOnCooldown}
                            cooldownEndsAt={cooldownEndsAt}
                            onViewAnalytics={handleViewAnalytics}
                        />
                    )}
                </StepCard>
            </BuilderGrid>

            {currentStep < 4 && (
                <SummaryCard>
                    <SummaryItem>
                        <SummaryLabel>{t(TK.status)}</SummaryLabel>
                        <CampaignStatusBadge status={campaign.status} />
                    </SummaryItem>
                    <SummaryDivider />
                    <SummaryItem>
                        <SummaryLabel>{t(TK.campaignBuilderLastUpdated)}</SummaryLabel>
                        <SummaryValue>
                            {campaign.lastUpdateDt ? moment(campaign.lastUpdateDt).format('DD/MM/YYYY HH:mm') : t(TK.notAvailable)}
                        </SummaryValue>
                    </SummaryItem>
                    <SummaryDivider />
                    <SummaryItem>
                        <SummaryLabel>{t(TK.campaignBuilderRecipients)}</SummaryLabel>
                        <SummaryValue>{campaign.lastRecipientEstimate ?? campaign.noOfRecipients ?? 0}</SummaryValue>
                    </SummaryItem>
                    {estimate?.requiresPayment && currentStep >= 2 && (
                        <>
                            <SummaryDivider />
                            <SummaryItem>
                                <SummaryLabel>{t(TK.campaignEstimatedCost)}</SummaryLabel>
                                <SummaryValue>{estimate.pricingTotal.toFixed(2)} {estimate.pricingCurrency}</SummaryValue>
                            </SummaryItem>
                        </>
                    )}
                    <SummaryDivider />
                    <SummaryHint>
                        {campaign.status === CampaignStatus.Draft
                            ? t(TK.campaignBuilderDraftHint)
                            : campaign.status === CampaignStatus.Ready
                                ? t(TK.campaignBuilderReadyHint)
                                : t(TK.campaignBuilderSentHint)}
                    </SummaryHint>
                </SummaryCard>
            )}

            <CampaignBuilderFooter
                currentStep={currentStep}
                canGoBack={currentStep > 1}
                canGoNext={currentStep < 4}
                canSave={!!campaign && !saving}
                canDiscard={!!campaign}
                canSendNow={canSendNow}
                canPayAndSend={canPayAndSend}
                canSendAfterPayment={canSendAfterPayment}
                canViewAnalytics={canViewAnalytics}
                loading={loading || saving || sending || paying}
                sendNowTooltip={isOnCooldown && cooldownEndsAt ? `${t(TK.campaignAnalyticsReTriggerNotEligible)} — ${t(TK.campaignAnalyticsEligibleFrom)} ${cooldownEndsAt}` : undefined}
                onBack={handleBack}
                onNext={handleNext}
                onSave={persistCampaign}
                onDiscard={handleDiscard}
                onSendNow={handleSendNow}
                onPayAndSend={handlePayAndSend}
                onViewAnalytics={handleViewAnalytics}
            />

            {/* Always mounted so the SDK can find the div immediately on startCheckout() */}
            <div style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.5)',
                zIndex: 1055,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: checkoutManifest ? 1 : 0,
                pointerEvents: checkoutManifest ? 'auto' : 'none',
                transition: 'opacity 0.2s ease',
            }}>
                <div style={{
                    background: 'white',
                    borderRadius: '8px',
                    padding: 'var(--spacer-M)',
                    maxWidth: '520px',
                    width: '90%',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                }}>
                    <div id="easypay-campaign-checkout" />
                </div>
            </div>

            <Modal isOpen={showTemplatesSettings} toggle={() => setShowTemplatesSettings(false)} size="lg">
                <ModalHeader toggle={() => setShowTemplatesSettings(false)}>
                    {t(TK.whatsappTemplates)}
                </ModalHeader>
                <ModalBody>
                    <WhatsAppTemplatesSettings id="campaign-builder-whatsapp-templates" values={null} onChange={() => {}} />
                </ModalBody>
            </Modal>
        </BuilderLayout>
    );
};

export default CampaignBuilderPage;
